import { Link } from "react-router-dom";
import { Building2, MapPin, Phone, Hash } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { Card, CardContent } from "../components/ui/card";
import { LogoMark, LogoWordmark } from "../components/Logo";
import { useLang } from "../context/LanguageContext";

// Fakta om leverandøren (oppgitt av eier – oversettes ikke)
export const PROVIDER = {
  name: "Kviteberg Digital",
  company: "Kviteberg Eiendom AS",
  address: "Røo 93, 5457 Høylandsbygd",
  phone: "40400759",
  phoneDisplay: "404 00 759",
  orgNumber: "937 009 240",
};

const FactRow = ({ icon: Icon, label, children, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary text-muted-foreground">
      <Icon className="w-4 h-4" aria-hidden="true" />
    </span>
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm font-medium text-foreground">{children}</p>
    </div>
  </div>
);

export default function AboutPage() {
  const { t } = useLang();
  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="about-page">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1 text-xs text-muted-foreground mb-4" data-testid="about-badge">
            <Building2 className="w-3.5 h-3.5" />
            {t("Om oss")}
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight" data-testid="about-title">
            {t("Om Right in")}
          </h1>
        </div>

        <Card className="border-border">
          <CardContent className="p-6 sm:p-8 space-y-8">
            {/* Logo + slagord */}
            <div className="flex flex-col items-center text-center gap-3 py-2" data-testid="about-logo-block">
              <LogoMark className="w-20 h-20" />
              <div className="flex flex-col items-center gap-1">
                <LogoWordmark className="text-4xl" />
                <p className="text-lg text-muted-foreground" data-testid="about-tagline">Enklere billettering</p>
              </div>
            </div>

            <p className="text-base leading-relaxed text-foreground" data-testid="about-provider-text">
              {t("En applikasjon levert av {name}, en avdeling i {company}, {address}, telefon {phone}, organisasjonsnummer {org}", {
                name: PROVIDER.name,
                company: PROVIDER.company,
                address: PROVIDER.address,
                phone: PROVIDER.phone,
                org: PROVIDER.orgNumber,
              })}
            </p>

            <div className="grid gap-4 sm:grid-cols-3 border-t border-border pt-6">
              <FactRow icon={MapPin} label={t("Adresse")} testId="about-fact-address">
                {PROVIDER.company}
                <br />
                {PROVIDER.address}
              </FactRow>
              <FactRow icon={Phone} label={t("Telefon")} testId="about-fact-phone">
                <a href={`tel:+47${PROVIDER.phone}`} className="underline underline-offset-4 hover:text-brand transition-colors duration-150" data-testid="about-phone-link">
                  {PROVIDER.phoneDisplay}
                </a>
              </FactRow>
              <FactRow icon={Hash} label={t("Organisasjonsnummer")} testId="about-fact-org">
                {PROVIDER.orgNumber}
              </FactRow>
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground mt-6 flex flex-wrap gap-x-4 gap-y-1">
          <Link to="/" className="underline underline-offset-4" data-testid="about-home-link">{t("Tilbake til forsiden")}</Link>
          <Link to="/vilkar" className="underline underline-offset-4" data-testid="about-terms-link">{t("Vilkår for kjøp")}</Link>
          <Link to="/personvern" className="underline underline-offset-4" data-testid="about-privacy-link">{t("Personvern og GDPR")}</Link>
        </p>
      </div>
    </PublicLayout>
  );
}
